/* =============================================================================
   mixins - the mixin system: composition instead of deep inheritance.
   Inheritance is exactly two levels (Component -> widget); shared CAPABILITIES
   are mixins, so a component only pays for what it uses:
     class Textbox extends mix(Component, FieldMixin) {}
   Exports: mix · FieldMixin · PopupMixin · ValidatableMixin.
   Dependencies: mix(), FieldMixin and PopupMixin depend on nothing but the DOM
   helpers (core/dom.js); ValidatableMixin bridges to ValidationService - the one
   sanctioned edge from the core into the services.
   There is no formatting mixin: number formatting and parsing are `Ics.number`,
   resolved through the page at call time, never a second definition here.
   Order matters: a mixin that overrides _destroy() (PopupMixin) calls super, so
   it must sit above any other mixin that also needs teardown.
   ============================================================================= */
import { cssVarNum, placePanel } from './dom.js';
import { ValidationService } from '../services/validation.js';

/** Compose a base class with mixin factories: mix(Component, A, B) === B(A(Component)) */
export function mix(Base, ...mixins) {
  return mixins.reduce((cls, m) => m(cls), Base);
}

/* -----------------------------------------------------------------------------
   FieldMixin - the value / disabled / readonly plumbing common to every input
   widget. A subclass stores its working <input> as this.input, or overrides
   _field(). Value semantics: getValue / setValue, where a programmatic setValue
   fires 'change' SYNCHRONOUSLY (and only when the value actually differs, string-
   compared). Adds: disable() · enable() · readonly(on) - each also toggling the
   ics-disabled / ics-readonly class on the component element.
   ---------------------------------------------------------------------------- */
export const FieldMixin = (Base) => class extends Base {
  /** the working form control; default = the bound element itself when it IS an input */
  _field() {
    return this.input || (/^(INPUT|SELECT|TEXTAREA)$/.test(this.el.tagName) ? this.el : null);
  }

  getValue() {
    const f = this._field();
    return f ? f.value : this._value;
  }

  /** programmatic set FIRES change synchronously (family-wide rule). null and
      undefined mean EMPTY - a field holds a string, never the word "undefined" -
      and setting empty into an empty field is not a change. */
  setValue(value) {
    const f = this._field();
    const old = this.getValue();
    const v = value == null ? '' : value;
    if (f) f.value = v; else this._value = v;
    if (String(old == null ? '' : old) !== String(v)) this.emit('change', { value: v, oldValue: old });
    return this;
  }

  disable() {
    const f = this._field();
    if (f) f.disabled = true;
    this.el.classList.add('ics-disabled');
    return this;
  }

  enable() {
    const f = this._field();
    if (f) f.disabled = false;
    this.el.classList.remove('ics-disabled');
    return this;
  }

  readonly(on = true) {
    const f = this._field();
    if (f) f.readOnly = on;
    this.el.classList.toggle('ics-readonly', on);
    return this;
  }
};

/* -----------------------------------------------------------------------------
   PopupMixin - the floating-panel behaviour shared by calendar, combo,
   autocomplete and tooltip panels: ONE dropdown z-band read from the tokens
   (--ics-z-dropdown, which always sits above any stacked window), outside-click
   and Escape close, anchor positioning with flip. The panel is built lazily by
   the subclass's _buildPanel() and appended to <body>, so it is never clipped by
   an overflow ancestor; it is removed again with the component.
   Adds: isOpen · openPanel() · closePanel() · togglePanel(), and emits
   'open' / 'close'. Outside-click is bound in the CAPTURE phase, so an inside
   click that calls stopPropagation still keeps the panel open.
   ---------------------------------------------------------------------------- */
export const PopupMixin = (Base) => class extends Base {
  get isOpen() { return !!this._panelOpen; }

  /** subclasses MUST implement: create + return the panel element (not yet attached) */
  _buildPanel() { throw new Error('PopupMixin: _buildPanel() not implemented'); }

  /** anchor for positioning; default = the component element */
  _popupAnchor() { return this.el; }

  /** Does this pointer event fall OUTSIDE the popup and its host? Subclasses whose
      visible host is a generated wrapper (combo, combotree: trigger + synthetic
      input live outside `el`) override this instead of re-binding the document
      listener - the listener is bound once, so an override is the only seam. */
  _popupOutsideHit(e) {
    return !this.panel.contains(e.target) && !this.el.contains(e.target);
  }

  openPanel() {
    if (this._panelOpen || this._disposed) return this;
    if (!this.panel) {
      this.panel = this._buildPanel();
      this.panel.classList.add('ics-popup');
      document.body.appendChild(this.panel);
    }
    this.panel.style.display = '';
    this.panel.style.zIndex = String(cssVarNum('--ics-z-dropdown') || 5000);
    this._placement = placePanel(this.panel, this._popupAnchor(), this.options.popup || {});
    this._panelOpen = true;
    /* Document listeners are bound ONCE per component, not once per open, and stay
       for the component's life; both handlers no-op while the panel is closed.
       Binding them per open would leak two document-level closures on EVERY open
       (EventBinder has no per-listener release, and destroy() rarely runs on a
       session-long screen), so every open would add handlers firing on every
       page-wide pointerdown and keydown. */
    if (!this._popupDocBound) {
      this._popupDocBound = true;
      // outside-click (capture, so inside clicks that stopPropagation still work) + ESC
      this._outside = this._events.add(document, 'pointerdown', (e) => {
        if (!this._panelOpen || !this.panel) return;
        if (this._popupOutsideHit(e)) this.closePanel();
      }, true);
      this._esc = this._events.add(document, 'keydown', (e) => {
        if (!this._panelOpen) return;
        if (e.key === 'Escape') this.closePanel();
      });
    }
    this.emit('open');
    return this;
  }

  closePanel() {
    if (!this._panelOpen) return this;
    this.panel.style.display = 'none';
    this._panelOpen = false;
    this.emit('close');
    return this;
  }

  togglePanel() { return this._panelOpen ? this.closePanel() : this.openPanel(); }

  /** teardown: the injected panel is removed with the component (leak-free rule) */
  _destroy() {
    if (this.panel && this.panel.parentNode) this.panel.parentNode.removeChild(this.panel);
    this.panel = null;
    super._destroy && super._destroy();
  }
};

/* -----------------------------------------------------------------------------
   ValidatableMixin - wires a component to ValidationService and to the shared
   validity state (the errorFieldBorder marker class). Rules come from the
   options (required / min / max / rules[]) plus the element's own required
   markup; validate() short-circuits on the FIRST failing rule, marks the field
   and emits 'validate' or 'invalid' with the result.
   Known limitation: this mixin renders no message bubble - the marker class and
   the emitted events are the whole contract; painting the message belongs to the
   popup component that shows it.
   ---------------------------------------------------------------------------- */
export const ValidatableMixin = (Base) => class extends Base {
  _rules() {
    const o = this.options, rules = [];
    const el = this._field ? this._field() : this.el;
    if (o.required || (el && ValidationService.isRequired(el))) rules.push({ name: 'required' });
    if (o.min !== undefined) rules.push({ name: 'min', param: o.min });
    if (o.max !== undefined) rules.push({ name: 'max', param: o.max });
    for (const r of o.rules || []) rules.push(r);
    return rules;
  }

  validate() {
    const el = this._field ? this._field() : this.el;
    const value = this.getValue ? this.getValue() : (el ? el.value : '');
    const res = ValidationService.validateField(el, value, this._rules());
    ValidationService.markInvalid(el || this.el, !res.valid);
    this.emit(res.valid ? 'validate' : 'invalid', res);
    return res.valid;
  }

  isValid() { return this.validate(); }
};
